// frontend/packages/business-console/src/components/content/MediaField.jsx

import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

import { buildCloudinaryUrl, fromCldToken } from '../../utils/mediaToken';
import { useMediaConfig } from '../../contexts/MediaConfigContext';
import useDirectUpload from '../../hooks/useDirectUpload';
import HelpLink from '../help/HelpLink';

const THUMB_SIZE = 88;

const IMAGE_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,image/svg+xml,image/avif';
const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime';

export default function MediaField({
  label,
  value,
  required,
  mediaType = 'image',
  helperText,
  onChoose,
  onClear,
  onChangeRaw,
}) {
  const publicId = useMemo(() => fromCldToken(value), [value]);
  const { cloudName } = useMediaConfig();
  const { upload, uploading } = useDirectUpload();
  const fileInputRef = useRef(null);
  // A failed upload used to log to the console and nothing else: the spinner
  // stopped, no image appeared, and the operator had no idea why. Surface it.
  const [uploadError, setUploadError] = useState('');

  const thumbUrl = useMemo(() => {
    if (!value || !String(value).startsWith('cld:')) return '';
    return buildCloudinaryUrl(value, cloudName, {
      width: THUMB_SIZE * 2,
      height: THUMB_SIZE * 2,
      mediaType,
    });
  }, [cloudName, mediaType, value]);

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadError('');

    try {
      const result = await upload(file);
      if (result?.token) {
        onChangeRaw?.(result.token);
      } else {
        setUploadError(
          'That file uploaded but came back without an id. Please try again.'
        );
      }
    } catch (err) {
      console.error('[MediaField] direct upload failed:', err);
      setUploadError(
        `That ${mediaType} did not upload. Check the file type and size (under 10 MB), then try again.`
      );
    }
  };

  const acceptTypes = mediaType === 'video' ? VIDEO_ACCEPT : IMAGE_ACCEPT;

  const displayHelper = uploading
    ? 'Uploading your file…'
    : helperText
      ? helperText
      : publicId
        ? `Click Replace to change this ${mediaType}`
        : `Add a ${mediaType} from your device or media library`;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ minWidth: 0 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
            {label}
          </Typography>
          <HelpLink topic="upload-image" />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={
              uploading ? (
                <CircularProgress size={16} />
              ) : (
                <FileUploadOutlinedIcon />
              )
            }
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            sx={{ fontWeight: 900 }}
          >
            {uploading ? 'Uploading…' : value ? 'Replace' : 'Upload'}
          </Button>

          <Button
            variant="outlined"
            startIcon={<PhotoLibraryOutlinedIcon />}
            onClick={onChoose}
            disabled={uploading}
            sx={{ fontWeight: 900 }}
          >
            Browse
          </Button>

          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ClearIcon />}
            onClick={onClear}
            disabled={!value || uploading}
            sx={{ fontWeight: 900 }}
          >
            Clear
          </Button>
        </Stack>
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptTypes}
        capture={undefined}
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />

      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: THUMB_SIZE,
            height: THUMB_SIZE,
            flexShrink: 0,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'action.hover',
            cursor: value ? 'pointer' : 'default',
          }}
          onClick={() => {
            if (!uploading) fileInputRef.current?.click();
          }}
          title={value ? 'Click to replace' : 'Click to upload'}
        >
          {uploading ? (
            <CircularProgress size={24} />
          ) : thumbUrl ? (
            mediaType === 'video' ? (
              <video
                src={thumbUrl}
                muted
                playsInline
                preload="metadata"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <img
                src={thumbUrl}
                alt=""
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )
          ) : (
            <ImageOutlinedIcon
              sx={{ color: 'action.disabled', fontSize: 32 }}
            />
          )}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}
          >
            {displayHelper}
          </Typography>
          {uploadError ? (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'error.main', mb: 0.5 }}
            >
              {uploadError}
            </Typography>
          ) : null}
          {publicId && (
            <Typography
              variant="caption"
              sx={{ opacity: 0.6, fontSize: 12, wordBreak: 'break-all' }}
            >
              {publicId.split('/').pop() || publicId}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
